import type { DragEvent, ReactNode } from 'react';
import { formatTimestamp } from '../../lib/format';
import { safeExternalUrl } from '../../lib/url';
import type { FeedSourceRead } from '../../types/api';
import {
  FEED_DISCOVERY_LABEL,
  FEED_KIND_LABEL,
  FEED_STATUS_LABEL,
  sourceAddress,
  sourceAge,
  sourceLabel,
} from './feedSourceLabels';

/** Which of the row's two facts is worth a badge on this particular list. */
export type FeedSourceRowBadge = 'status' | 'discovery';

/**
 * Native HTML5 drag, as the calendar's chips do it. Passed only by the
 * categories board, where a row is dragged from one card to another; every
 * other list of sources is a read-out and leaves this undefined.
 */
export interface FeedSourceRowDrag {
  onDragStart: (event: DragEvent<HTMLLIElement>) => void;
  onDragEnd: () => void;
  /** True while this row is the one being carried. Dims it in place. */
  isDragging: boolean;
}

interface FeedSourceRowProps {
  source: FeedSourceRead;
  /**
   * `status` is how the source is holding up, as under the headlines; `discovery`
   * is how it got here, which is the interesting fact on a curation receipt
   * where every row was written seconds ago.
   */
  badge: FeedSourceRowBadge;
  /** Trailing control, e.g. Unfollow. Omitted where the list is a read-out. */
  action?: ReactNode;
  /** Makes the row draggable. See {@link FeedSourceRowDrag}. */
  drag?: FeedSourceRowDrag;
}

/**
 * A press on the address is a press on the address. The row above it is a drag
 * handle on the categories board, and a link inside a draggable element is
 * otherwise picked up along with it.
 */
function keepInsideTheLink(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

/**
 * One followed source: name, the address it is read from, how fresh it is, and
 * one badge.
 *
 * The row shape shared by every list of sources inside the Edit feed dialog —
 * what you already follow, what a curation run just added, and what sits in a
 * category. The dialog is now the only place sources are shown at all, so this
 * is the only row there is: the same rows from the same collection, differing
 * in the badge, the trailing control and whether the row can be picked up.
 *
 * Two lines, not one. The name alone does not say which of a publisher's feeds
 * this is, nor whether there is a feed at all, so the address sits under it as
 * a real link — the `feed` / `page` prefix is what tells the reader we are
 * scraping a page because the site publishes nothing better. The age of the
 * newest item rides on the right with the badge, because "Active" and "8
 * months ago" together are the whole health of a source and either one alone
 * is misleading.
 */
export function FeedSourceRow({ source, badge, action, drag }: FeedSourceRowProps) {
  const classes = ['feed-following__item'];
  if (drag) classes.push('feed-following__item--draggable');
  if (drag?.isDragging) classes.push('feed-following__item--dragging');

  // Untrusted like any other address off a feed: a row that does not speak
  // http(s) is still named and still shown, just not as something to click.
  const href = safeExternalUrl(source.feed_url);
  const address = (
    <>
      <span className="feed-following__kind">{FEED_KIND_LABEL[source.kind]}</span>{' '}
      {sourceAddress(source)}
    </>
  );

  return (
    <li
      className={classes.join(' ')}
      draggable={drag !== undefined}
      onDragStart={drag?.onDragStart}
      onDragEnd={drag?.onDragEnd}
    >
      <span className="feed-following__name">{sourceLabel(source)}</span>

      {href ? (
        <a
          className="feed-following__address"
          href={href}
          target="_blank"
          rel="noreferrer"
          title={source.feed_url}
          // The link opens the feed and does nothing else: it is not the row's
          // drag handle and there is nothing on this row to toggle.
          draggable={false}
          onMouseDown={keepInsideTheLink}
          onDragStart={keepInsideTheLink}
        >
          {address}
        </a>
      ) : (
        <span className="feed-following__address" title={source.feed_url}>
          {address}
        </span>
      )}

      <span className="feed-following__meta">
        {source.last_item_at === null ? (
          <span className="feed-following__age">{sourceAge(source)}</span>
        ) : (
          <time
            className="feed-following__age"
            dateTime={source.last_item_at}
            title={`Newest item ${formatTimestamp(source.last_item_at)}`}
          >
            {sourceAge(source)}
          </time>
        )}
        {badge === 'status' ? (
          <span className={`badge badge--${source.status}`}>
            {FEED_STATUS_LABEL[source.status]}
          </span>
        ) : (
          <span className="feed-following__tag">{FEED_DISCOVERY_LABEL[source.discovered_via]}</span>
        )}
        {action ? <span className="feed-following__action">{action}</span> : null}
      </span>
    </li>
  );
}
